import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { ConfigLayerWFSOptions } from './ConfigLayerWFS'
import type { ConfigLayerOptions } from '../ConfigLayer'

import * as PoziUtils from 'api/utils'

import { ConfigLayerCapability } from './ConfigLayerCapability'
import { ConfigLayerCatalog } from './ConfigLayerCatalog'
import { ConfigLayerWFS } from './ConfigLayerWFS'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerCapabilityWFSOptions extends ConfigLayerOptions {
  layers: ConfigLayerWFSOptions[]
  outputFormats?: Record<string, string>
  type: ConfigLayerType.WFS_GET_CAPABILITIES
}

export class ConfigLayerCapabilityWFS extends ConfigLayerCatalog {
  static async fetchCapability (capabilityOptions: ConfigLayerCapabilityOptions) {
    const url = PoziUtils.getLayerUrl(capabilityOptions)

    if (url) {
      try {
        const text = await ConfigLayerCapability.fetchUrl(url)
        if (text) {
          const wfsCapabilities = this.getCapabilities(text)
          if (wfsCapabilities) {
            const configLayerCapabilityWFSOptions: ConfigLayerCapabilityWFSOptions = {
              type: ConfigLayerType.WFS_GET_CAPABILITIES,
              title: capabilityOptions.title,
              titleid: capabilityOptions.titleid,
              group: capabilityOptions.group,
              groupid: capabilityOptions.groupid,
              layers: this.getLayers(capabilityOptions, wfsCapabilities),
              outputFormats: this.getOutputFormats(wfsCapabilities)
            }

            console.log('WFS capability output', capabilityOptions.titleid, configLayerCapabilityWFSOptions)
            return configLayerCapabilityWFSOptions
          }
        }
      } catch (error) {
        console.error('Error fetching capabilities: ', url, error)
      }
    } else {
      console.error('Error getting url property from config to request WFS GetCapabilities: ', capabilityOptions)
    }
  }

  private static getCapabilities (text: string): Element {
    const xmlParser = new DOMParser()
    const xmlDoc = xmlParser.parseFromString(text, 'application/xml')
    const wfsCapabilities = xmlDoc.getElementsByTagName('wfs:WFS_Capabilities')?.[0] ?? xmlDoc.getElementsByTagName('WFS_Capabilities')?.[0]
    // console.debug('WFS Capabilities: ', wfsCapabilities)
    return wfsCapabilities
  }

  private static getFeatureTypes (wfsCapabilities: Element): Element[] {
    const featureTypes = wfsCapabilities.getElementsByTagName('FeatureType')
    // console.debug('layers: ', featureTypes)
    if (!featureTypes) {
      console.error('Error getting layers array from Catalog layer', wfsCapabilities)
      return []
    } else {
      const layers = Array.from(featureTypes)
      return layers
    }
  }

  private static getLayers (capabilityOptions: ConfigLayerCapabilityOptions, wfsCapabilities: Element) {
    let configObjs: ConfigLayerWFSOptions[] = []
    const featureTypes = this.getFeatureTypes(wfsCapabilities)

    // For layer filtering below
    const hasLayerFilter = this.hasLayerFilter(capabilityOptions)
    const layerFilterArray = this.getLayerFilterArray(capabilityOptions)

    const outputFormats = this.getOutputFormats(wfsCapabilities)

    // Keep track of layers that did not match filter and report them
    const unMatchedLayers: string[] = []

    featureTypes.forEach((layer: Element, index: number) => {
      const configLayer = ConfigLayerWFS.getLayer(capabilityOptions, wfsCapabilities, layer, index)

      // Do not add layers marked as `enabled: false` (usually from keywords)
      const layerEnabled = configLayer.enabled ?? true
      if (!layerEnabled) {
        console.debug(`ConfigLayerCapabilityWFS - getLayers - ${capabilityOptions.titleid} - layer is not enabled: '${configLayer.titleid}' (${configLayer.type}). Not adding.`)
        return
      }

      // Determine whether this layer matches any provided layer filter and ignore if
      // there is no match
      if (hasLayerFilter) {
        const isMatch = this.layerMatchesFilter(configLayer, layerFilterArray)
        if (!isMatch) {
          unMatchedLayers.push(configLayer.title)

          return
        }
      }

      // Store WFS GetFeature output formats in layerConf
      configLayer.config.spatial.outputFormats = outputFormats

      // console.debug('ConfigLayerCapabilityWFS - getLayers - layer', configLayer)

      configObjs.push(configLayer)
    })

    if (unMatchedLayers.length > 0) {
      console.debug(`ConfigLayerCapabilityWFS getLayers - ${capabilityOptions.titleid} - ${unMatchedLayers.length} layers did not match filter '${capabilityOptions.layerFilter}'`, unMatchedLayers)
    }

    configObjs = this.postProcessLayers(capabilityOptions, configObjs)

    return configObjs
  }

  private static getOutputFormats (wfsCapabilities: Element) {
    // Try to find the 'outputFormat' values in the capabilities
    const selector = 'WFS_Capabilities OperationsMetadata Operation[name="GetFeature"] Parameter[name="outputFormat"] Value'
    const queryResult = wfsCapabilities.querySelectorAll(selector)
    const outputFormatsArr = [...queryResult]
      .map(node => node.textContent) // extract the text value from the node
      .filter(text => !!text) as string[] // filter out any possible 'null' values and tell typescript it's all cool

    // See: https://gisgeography.com/gis-formats/
    // NOTE: The first word will become the file extension if the mime-type cannot
    //       be found in mime-db and non-word characters will be replaced by dots
    //
    //       Example: 'SHP-ZIP' will become '.shp.zip'
    const outputFormatAliases = {
      CSV: ['csv', 'text/csv'],
      'DWG - AutoCAD Drawing': ['dwg'],
      'DXF - AutoCAD Drawing Exchange Format': ['dxf'],
      'FGB - FlatGeobuf': ['FGB'],
      GeoJSON: ['GEOJSON', 'json', 'application/json', 'application/vnd.geo+json'],
      'GML 2.1.2': ['gml2', 'text/xml; subtype=gml/2.1.2'],
      'GML 3.1.1': ['gml3', 'gml', 'text/xml; subtype=gml/3.1.1', 'text/xml; subType=gml/3.1.1/profiles/gmlsf/1.0.0/0'],
      'GML 3.2.1': ['gml32', 'text/xml; subtype=gml/3.2', 'application/gml+xml; version=3.2', 'text/xml; subtype=gml/3.2.1'],
      'GPKG - OGC GeoPackage': ['GPKG'],
      'GPX - GPS eXchange Format': ['GPX'],
      KML: [
        'kml',
        'application/vnd.google-earth.kml+xml',
        'application/vnd.google-earth.kml xml'
      ],
      KMZ: [
        'application/vnd.google-earth.kmz'
      ],
      'MIF-ZIP - MapInfo Interchange File': ['MIF', 'mif-zip'],
      'ODS - Open Document Spreadsheet': ['ODS'],
      'SHP-ZIP - Esri Shapefile': ['shp', 'shape-zip'],
      'TAB-ZIP - MapInfo Table': ['TAB', 'tab-zip'],
      'XLSX - MS Office Open XML Spreadsheet': ['XLSX']
    }

    // Create a lookup table first to make it easier to create the
    // outputFormats object
    const aliasesLookupTable: Record<string, string> = {}
    for (const [label, aliases] of Object.entries(outputFormatAliases)) {
      for (const alias of aliases) {
        aliasesLookupTable[alias.toLowerCase()] = label
      }
    }

    // console.debug('WFS - Aliases lookup table ', aliasesLookupTable)

    // Finally, create the outputFormats object
    const outputFormats: Record<string, string> = {}
    for (const outputFormat of outputFormatsArr) {
      const label = aliasesLookupTable[outputFormat.toLowerCase()] || outputFormat
      outputFormats[label] = outputFormat
    }

    // console.debug('ConfigLayerCapabilityWFS getOutputFormats', outputFormatsArr, outputFormats, wfsCapabilities)

    return outputFormats
  }
}
